var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Box, SelectionList, Text, TextWithHighlight, Tooltip, } from '@dixa/lib-shared-habla';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDeepCompareEffect } from 'react-use';
import { HookFormSelectionList } from 'components/hookForm';
import { useNotifications } from 'hooks/useNotifications';
import { getCustomSelectFieldDisplayValue } from 'utils/customFields';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { convertTreeToFlatItems } from 'utils/tree';
import { getConversationCustomFieldsFormWarningMessage } from '../../utils/conversationCustomFieldsFormWarningMessage';
import { errorNotification } from '../../utils/notification';
import { useSaveStatusIcon } from '../../utils/useSaveStatusIcon';
import styles from './customFieldSelect.module.scss';
const pathSeparator = ' / ';
/**
 * Renders a hook form select field to display/edit a custom field of type
 * select.
 */
export const CustomFieldSelect = memo(function CustomFieldSelect({ isAcw, customField, save, }) {
    const { attribute, value } = customField;
    const { requiredCustomAttribute } = useFeatureFlag([
        'required-custom-attribute',
    ]);
    const isRequired = requiredCustomAttribute && attribute.is_required;
    const { options, label, id, description, data_type } = attribute;
    const [filter, setFilter] = useState('');
    const notify = useNotifications();
    const [icon, setStatus] = useSaveStatusIcon();
    const savedValue = useMemo(() => ((value === null || value === void 0 ? void 0 : value.value) ? [value === null || value === void 0 ? void 0 : value.value] : undefined), [value === null || value === void 0 ? void 0 : value.value]);
    const formSavedValues = useMemo(() => ({ [id]: savedValue }), [id, savedValue]);
    const { control, getFieldState, reset, setValue } = useForm({
        mode: 'onChange',
        defaultValues: formSavedValues,
        shouldFocusError: false,
    });
    useEffect(() => {
        if (isAcw) {
            // A trick to make the field `touched` and `dirty`, so that the warning
            // will be displayed when in wrap-up mode.
            // Using setTimeout because sometimes the field is not already rendered
            setTimeout(() => {
                setValue(id, savedValue, {
                    shouldDirty: true,
                    shouldTouch: true,
                });
            });
        }
    }, [id, isAcw, savedValue, setValue]);
    const handleChange = useCallback((changeEvent) => __awaiter(this, void 0, void 0, function* () {
        const newValue = changeEvent === null || changeEvent === void 0 ? void 0 : changeEvent.selectedValues;
        const fieldState = getFieldState(id);
        if (!fieldState.error && !isEqual(savedValue, newValue[0])) {
            const patch = {
                before: (value === null || value === void 0 ? void 0 : value.value)
                    ? {
                        [id]: {
                            data_type,
                            value: value === null || value === void 0 ? void 0 : value.value,
                        },
                    }
                    : {},
                after: newValue.length
                    ? {
                        [id]: {
                            data_type,
                            value: newValue[0],
                        },
                    }
                    : {},
            };
            setStatus('none');
            try {
                yield save(attribute, patch);
                setStatus('success');
            }
            catch (_a) {
                reset(formSavedValues);
                setStatus('error');
                notify(errorNotification);
            }
        }
    }), [
        attribute,
        data_type,
        formSavedValues,
        getFieldState,
        id,
        notify,
        reset,
        save,
        savedValue,
        setStatus,
        value === null || value === void 0 ? void 0 : value.value,
    ]);
    useDeepCompareEffect(() => {
        reset(formSavedValues);
    }, [formSavedValues]);
    const helpIcon = useMemo(() => ({
        text: description,
        dark: true,
    }), [description]);
    const getItemTitleByValue = useCallback(() => {
        const displayValue = getCustomSelectFieldDisplayValue(customField, {
            nodeClassName: styles.subdued,
            separatorClassName: styles.subdued,
            pathSeparator,
        });
        if (!displayValue) {
            return '';
        }
        return (_jsx(Tooltip, Object.assign({ dark: true, wrapProps: {
                className: styles.tooltipWrapper,
            }, content: displayValue.rawPath, disabled: !displayValue.isLongPath }, { children: _jsx(Box, Object.assign({ className: styles.selectionTitle }, { children: _jsx(Box, Object.assign({ as: "span", className: styles.selectionTitleContent }, { children: displayValue.path })) })) })));
    }, [customField]);
    const renderListOrItem = useCallback((option, valuePath = []) => {
        var _a;
        if ((_a = option.options) === null || _a === void 0 ? void 0 : _a.length) {
            return (_jsx(SelectionList.List, Object.assign({ parentItem: _jsx(SelectionList.Item, { children: _jsx(Text.Paragraph, { children: option.label }) }) }, { children: [...option.options]
                    .filter(({ is_deactivated }) => !is_deactivated)
                    .map((childOption) => renderListOrItem(childOption, [...valuePath, option.value])) }), option.id));
        }
        return (_jsx(SelectionList.Item, Object.assign({ value: [...valuePath, option.value] }, { children: _jsx(Text.Paragraph, { children: option.label }) }), option.id));
    }, []);
    const renderListItems = useCallback(() => [...options]
        .filter(({ is_deactivated }) => !is_deactivated)
        .map((option) => renderListOrItem(option)), [options, renderListOrItem]);
    const inputSelectionListProps = useMemo(() => ({
        clearable: !isRequired,
        icon,
        getItemTitleByValue,
        onChange: handleChange,
        equalityFn: isEqual,
        filter: {
            value: filter,
            onChange: (event) => {
                setFilter(event.target.value);
            },
        },
        onToggleDropdown: (isOpen) => isOpen && setFilter(''),
        selectionListProps: {
            className: styles.selectionList,
        },
    }), [filter, icon, isRequired, getItemTitleByValue, handleChange]);
    const renderFilteredListItems = useCallback(() => convertTreeToFlatItems(options, {
        childrenKey: 'options',
        transformItem: (option, path) => (Object.assign(Object.assign({}, option), { label: path
                .map(({ label }) => label)
                .concat([option.label])
                .join(pathSeparator), value: path.map(({ value }) => value).concat([option.value]) })),
    })
        .filter(({ is_deactivated, label, options: childOptions }) => !is_deactivated &&
        !(childOptions === null || childOptions === void 0 ? void 0 : childOptions.length) &&
        label.toLowerCase().includes(filter.toLowerCase()))
        .map((option) => (_jsx(SelectionList.Item, Object.assign({ value: option.value }, { children: _jsx(Text.Paragraph, { children: _jsx(TextWithHighlight, { text: option.label, highlight: filter }) }) }), option.id))), [filter, options]);
    return (_jsx(HookFormSelectionList, Object.assign({ control: control, label: label, name: id, rules: isRequired ? { required: true } : {}, helpIcon: helpIcon, componentProps: inputSelectionListProps, meta: {
            warning: getConversationCustomFieldsFormWarningMessage(customField, isAcw, requiredCustomAttribute),
        } }, { children: filter ? renderFilteredListItems() : renderListItems() })));
});
